import { observer } from "mobx-react-lite";
import { MdsSpacer } from "@/design-system/components/spacer";
import { css } from "@/domains/emotion";
import { useAppStore, useGuestAppStore } from "@/store";
import { MdsIcon, MdsIconKind } from "@/design-system/components/icon";
import { NoteEditorTopBarShareButton } from "@/components/note/editor/top-bar/NoteEditorTopBarShareButton";
import { NoteEditorTopBarExtraActionsDropdownButton } from "@/components/note/editor/top-bar/NoteEditorTopBarExtraActionsDropdownButton";
import { NoteTopBarTrashedNoteBanner } from "@/components/note/editor/top-bar/NoteTopBarTrashedNoteBanner";
import { AnimationTiming } from "@/design-system/constants/common";
import { ZIndex } from "@/domains/design/constants";
import { INoteObservable } from "@/store/note/types";
import { PageElementRouter } from "@/app/outlets/PageElementRouter";
import styled from "@emotion/styled";
import { MdsItemListRowFeaturedContent } from "@/design-system/components/item-list/rows/MdsItemListRowFeaturedContent";
import { sortCollectionsForChips } from "@/domains/collections/sortCollectionsForChips";
import { mdsPanelWidths } from "@/design-system/foundations";
import { OrganizeNote, useOrganizeNoteState } from "@/components/note/editor/top-bar/organize";
import { AboutNoteModal } from "@/components/note/editor/top-bar/about/AboutNoteModal";
import { useState } from "react";
import {
  MDSButton,
  MDSButtonSlotName,
  MDSButtonVariant,
  MDSIconButton,
  MDSIconButtonSize,
  MDSVerticalDivider,
} from "@/mds";
import { MDSTooltip, MDSTooltipPlacement } from "@/mds/components/tooltip";
import { NoteObservable } from "@/store/note/NoteObservable";
import { RecordingStatus } from "@/store/audio-capture/AppStoreAudioCaptureStore";
import { MemCommonEditorStore } from "@/components/note/store/MemCommonEditorStore";
import { VoiceIndicator } from "@/components/note/editor/top-bar/indicators/VoiceIndicator";
import { CleanUpIndicator } from "@/components/note/editor/top-bar/indicators/CleanUpIndicator";
import { NoteVersionHistoryMenuBar } from "@/components/note/version-history";
import { useTabPageStore } from "@/hooks/useTabPageStore";

interface NoteEditorTopBarProps {
  editorStore?: MemCommonEditorStore;
  note?: INoteObservable;
  containerWidth: number;
}

export const NoteEditorTopBar = observer<NoteEditorTopBarProps>(function NoteEditorTopBar({
  editorStore,
  note,
  containerWidth,
}) {
  return (
    <PageElementRouter
      standardModeElement={
        <StandardNoteComboBar
          editorStore={editorStore}
          note={note}
          containerWidth={containerWidth}
        />
      }
      guestModeElement={<GuestNoteEditorTopBar note={note} containerWidth={containerWidth} />}
    />
  );
});

const StandardNoteComboBar = observer<NoteEditorTopBarProps>(function StandardNoteComboBar({
  editorStore,
  note,
  containerWidth,
}) {
  const page = useTabPageStore("notesViewPage");

  const isVersionHistoryActive =
    page.noteVersionHistory.currentNoteId && page.noteVersionHistory.currentNoteId === note?.id;

  return (
    <>
      <div style={{ display: isVersionHistoryActive ? "none" : "block" }}>
        <StandardNoteEditorTopBar
          editorStore={editorStore}
          note={note}
          containerWidth={containerWidth}
        />
      </div>
      <div style={{ display: isVersionHistoryActive ? "block" : "none" }}>
        <StandardNoteVersionHistoryTopBar
          editorStore={editorStore}
          note={note}
          containerWidth={containerWidth}
        />
      </div>
    </>
  );
});

const StandardNoteVersionHistoryTopBar = observer<NoteEditorTopBarProps>(
  function StandardNoteVersionHistoryTopBar({
    editorStore: _editorStore,
    note: _note,
    containerWidth: _containerWidth,
  }) {
    return (
      <>
        <Container $variant="version-history">
          <NoteVersionHistoryMenuBar />
        </Container>
      </>
    );
  }
);

const StandardNoteEditorTopBar = observer<NoteEditorTopBarProps>(function StandardNoteEditorTopBar({
  editorStore,
  note,
  containerWidth,
}) {
  const { store } = useAppStore();

  const canStartRecording = store.audioCapture.status === RecordingStatus.IDLE;

  const notesViewPage = useTabPageStore("notesViewPage");
  const organizeNoteState = useOrganizeNoteState();
  const [aboutModalOpen, setAboutModalOpen] = useState(false);

  const isCollapsed = containerWidth <= mdsPanelWidths().narrow;

  const hasSuggestion = !!note?.suggestionData;

  const isVoiceActive = store.audioCapture.activeNoteId === note?.id;
  const isCleanUpActive = !isVoiceActive && editorStore?.noteObservable?.isSuggestionActive;

  if (!note) return null;
  if (note.isTrashed) return <NoteTopBarTrashedNoteBanner note={note} />;

  const collections = note.collectionList?.allCollections || [];

  return (
    <>
      <Container $variant={hasSuggestion ? "suggestion" : "default"}>
        <MdsItemListRowFeaturedContent
          compact
          collections={sortCollectionsForChips(collections)}
          itemId={note.id}
        />
        <OrganizeNote note={note} {...organizeNoteState}>
          {!collections.length && (
            <MDSButton
              variant={MDSButtonVariant.Subtle}
              onClick={organizeNoteState.toggleOrganizeIsOpen}
            >
              <MdsIcon kind={MdsIconKind.Collection} slot={MDSButtonSlotName.LeftIcon} />
              Organize
            </MDSButton>
          )}
          {!!collections.length && (
            <MDSIconButton
              iconKind={MdsIconKind.Plus}
              onClick={organizeNoteState.toggleOrganizeIsOpen}
            />
          )}
        </OrganizeNote>
        <Spacer />

        <>
          {note instanceof NoteObservable && (
            <IconsContainer>
              {isVoiceActive && editorStore && <VoiceIndicator editorStore={editorStore} />}
              {isCleanUpActive && <CleanUpIndicator editorStore={editorStore} />}
              <MDSTooltip
                config={{
                  label: "Start voice recording",
                  placement: MDSTooltipPlacement.Top,
                }}
              >
                <MDSIconButton
                  size={MDSIconButtonSize.Medium}
                  iconKind={MdsIconKind.Microphone}
                  isDisabled={!canStartRecording}
                  onClick={() => {
                    store.audioCapture.startRecording({ noteId: note.id });
                  }}
                />
              </MDSTooltip>
              {!isCollapsed && (
                <MDSTooltip
                  config={{
                    label: "Clean up notes in a single click",
                    placement: MDSTooltipPlacement.Top,
                  }}
                >
                  <MDSIconButton
                    size={MDSIconButtonSize.Medium}
                    iconKind={
                      note.isProcessingSuggestion ? MdsIconKind.Spinner : MdsIconKind.Cleanup
                    }
                    isDisabled={note.isProcessingSuggestion}
                    onClick={() => note.cleanup()}
                  />
                </MDSTooltip>
              )}
              {!isCollapsed && <MDSVerticalDivider />}
            </IconsContainer>
          )}
          {!isCollapsed && (
            <MDSTooltip
              config={{
                label: note.isFavorited ? "Unpin from sidebar" : "Pin to sidebar",
                placement: MDSTooltipPlacement.Top,
                delaySeconds: AnimationTiming.TooltipHoverDelaySeconds,
              }}
            >
              <MDSIconButton
                size={MDSIconButtonSize.Medium}
                iconKind={note?.isFavorited ? MdsIconKind.ThumbtackSolid : MdsIconKind.Thumbtack}
                onClick={async () => await note?.toggleFavorite()}
                isDisabled={!note}
              />
            </MDSTooltip>
          )}
          <NoteEditorTopBarExtraActionsDropdownButton
            note={note}
            shareSheetModal={notesViewPage.shareSheetModal}
          />
        </>
      </Container>

      <AboutNoteModal
        isOpen={aboutModalOpen}
        onClose={() => setAboutModalOpen(false)}
        note={note}
      />
    </>
  );
});

const GuestNoteEditorTopBar = observer<NoteEditorTopBarProps>(function GuestNoteEditorTopBar({
  note,
}) {
  const { guestStore } = useGuestAppStore();
  return (
    <div className={topBarStyles}>
      <MdsSpacer />
      <NoteEditorTopBarShareButton note={note} shareSheetModal={guestStore.shareSheetModal} />
    </div>
  );
});

export const NOTE_EDITOR_TOP_BAR_HEIGHT = "40px";

const topBarStyles = css({
  alignItems: "center",
  display: "flex",
  gap: "4px",
  height: NOTE_EDITOR_TOP_BAR_HEIGHT,
  left: 0,
  // Use padding, not margin.
  padding: "6px 16px 0 16px",
  position: "relative",
  top: 0,
  width: "100%",
  zIndex: ZIndex.NavBar, // Above the editor so it scrolls under.

  "@media (max-width: 900px)": {
    padding: "0 8px",
  },
});

const Container = styled.div<{ $variant?: "default" | "suggestion" | "version-history" }>`
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: ${({ theme }) => `${theme.spacing["2"]} ${theme.spacing["5"]} 0 ${theme.spacing["7"]}`};
  ${({ $variant, theme }) =>
    $variant === "suggestion" && `background-color: ${theme.colors.background.diff};`};
  ${({ $variant, theme }) =>
    $variant === "version-history" &&
    `background-color: ${theme.colors.background.banner.emphasis};`};

  ${({ theme }) => `
    ${theme.mediaQueries.rules.phone} {
      padding-left: ${theme.spacing[4]};
      padding-right: ${theme.spacing[4]};
    }
    `}
`;

const Spacer = styled.div`
  flex: 1;
`;

const IconsContainer = styled.div`
  display: flex;
  align-items: center;
`;
